import {fromPairs, mapKeys} from 'lodash'

export interface ChoiceFacetOption {
  value: number
  label: string
  count: number
  active: boolean
}

export interface HierarchicalFacetOption extends ChoiceFacetOption {
  parent: number
}

export interface FacetTreeItem extends HierarchicalFacetOption {
  children?: FacetTreeItem[]
  parents?: FacetTreeItem[]
}

export interface FacetOrderValues {
  [facet: string]: {
    order?: string[]
    sortRemainingBy?: 'count' | 'alpha' | 'hidden'
  }
}

export const facetNames = (attrNameMap: Record<string, string>) => ({
  categories: 'Flokkar',
  priceRange: 'Verð',
  ...mapKeys(attrNameMap ?? {}, (_, k) => `attributes.${k}`),
})

export function sortFacetOptions(
  key: string,
  options: ChoiceFacetOption[] | HierarchicalFacetOption[],
  facetOrderValues: FacetOrderValues,
): ChoiceFacetOption[] | HierarchicalFacetOption[] {
  if (!facetOrderValues) {
    return options
  }
  const order = facetOrderValues[key]?.order
  const remainingOrder = facetOrderValues[key]?.sortRemainingBy

  const sortOptionsMap = fromPairs(options.map((o) => [o.label, o]))

  const orderedOptions =
    order?.map((o) => sortOptionsMap[o]).filter((x) => x) || []
  const remainingOptions = options?.filter((f) => !order?.includes(f.label))

  if (remainingOptions?.length && remainingOrder === 'alpha') {
    remainingOptions.sort((a, b) => a.label.localeCompare(b.label))
  } else if (remainingOptions?.length && remainingOrder === 'count') {
    remainingOptions.sort((a, b) => b.count - a.count)
  }

  return [...orderedOptions, ...remainingOptions]
}

export function convertToFacetTree(
  facets: HierarchicalFacetOption[],
): Map<string | number, FacetTreeItem> {
  const facetPairs = facets.map<[string | number, FacetTreeItem]>((f) => [
    f.value,
    {...f, children: [], parents: []},
  ])

  const tree = new Map(facetPairs)

  tree.forEach((ref: FacetTreeItem) => {
    tree.get(ref.parent)?.children.push(ref)
  })

  tree.forEach((ref: FacetTreeItem) => {
    ref.children?.forEach((child) => {
      child.parents.push(tree.get(child.parent))
    })
  })

  return tree
}
